import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { HeartHandshake, ClipboardList, Check, X, Clock, MapPin, Calendar, User, ArrowRight } from "lucide-react";

const STATUS_COLOR = {
  solicitada: "bg-slate-100 text-slate-700",
  em_analise: "bg-blue-50 text-blue-700",
  aguardando_profissional: "bg-amber-50 text-amber-700",
  confirmada: "bg-emerald-50 text-emerald-700",
  realizada: "bg-emerald-100 text-emerald-800",
  cancelada: "bg-red-50 text-red-700",
};

export default function FPProfessionalArea() {
  const [me, setMe] = useState(null);
  const [notLinked, setNotLinked] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [profile, reqs] = await Promise.all([
        api.get("/fp-pro/me"),
        api.get("/fp-pro/requests"),
      ]);
      setMe(profile.data);
      setRequests(reqs.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotLinked(true);
      } else {
        toast.error("Erro ao carregar dados");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const accept = async (rid) => {
    try {
      await api.patch(`/fp-pro/requests/${rid}/accept`, {});
      toast.success("Solicitação aceita — família notificada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const decline = async (rid) => {
    if (!window.confirm("Recusar essa solicitação? Ela voltará para a Central RAF buscar outro profissional.")) return;
    try {
      await api.patch(`/fp-pro/requests/${rid}/decline`, {});
      toast.success("Recusada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;

  if (notLinked) return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="fp-pro-not-linked">
      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
        <HeartHandshake className="w-7 h-7 text-amber-600" />
      </div>
      <h1 className="font-display text-2xl font-bold text-[#0B2B62] mb-2">Ainda não temos o seu perfil</h1>
      <p className="text-slate-600 mb-6">
        Para acessar a Área do Profissional Filho Presente, você precisa se cadastrar e aguardar a aprovação da nossa equipe.
      </p>
      <Link to="/filho-presente/cadastre-profissional"
        data-testid="fp-pro-cta-apply"
        className="inline-flex items-center gap-2 bg-[#0B2B62] text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 text-sm">
        Quero me cadastrar <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );

  const open = requests.filter((r) => ["solicitada", "em_analise", "aguardando_profissional"].includes(r.status));
  const upcoming = requests.filter((r) => r.status === "confirmada");
  const done = requests.filter((r) => r.status === "realizada");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-pro-area">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
        <HeartHandshake className="w-3.5 h-3.5 text-amber-500" /> Área do Profissional · Filho Presente
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62]">Olá, {me?.display_name?.split(" ")[0]}</h1>
      <p className="text-slate-600 text-sm mt-1 mb-6">
        {me?.category}{me?.specialty && ` · ${me.specialty}`}
        {me?.city && ` · ${me.city}${me.state ? "/" + me.state : ""}`}
      </p>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <Kpi label="Solicitações abertas" value={open.length} />
        <Kpi label="Próximos atendimentos" value={upcoming.length} />
        <Kpi label="Concluídos" value={done.length} />
      </div>

      <Section title="Solicitações aguardando sua confirmação" items={open} empty="Nenhuma solicitação nova no momento."
        renderActions={(r) => (
          <>
            <button onClick={() => accept(r.id)} data-testid={`fp-pro-accept-${r.id}`}
              className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Aceitar
            </button>
            <button onClick={() => decline(r.id)} data-testid={`fp-pro-decline-${r.id}`}
              className="text-xs bg-white border border-red-300 text-red-600 px-3 py-1.5 rounded-md inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Recusar
            </button>
          </>
        )} />

      <Section title="Próximos atendimentos" items={upcoming} empty="Sem atendimentos confirmados." />

      <Section title="Histórico" items={done.slice(0, 20)} empty="Ainda sem histórico." />
    </div>
  );
}

const Kpi = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-2xl font-bold text-[#0B2B62]">{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);

const Section = ({ title, items, empty, renderActions }) => items.length === 0 ? (
  <section className="mb-8">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">{title}</div>
    <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500 text-center">{empty}</div>
  </section>
) : (
  <section className="mb-8">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">{title} · {items.length}</div>
    <div className="bg-white border border-slate-200 rounded-xl divide-y">
      {items.map((r) => (
        <div key={r.id} className="p-4 flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800">{r.beneficiary_name}</span>
              {r.priority === "urgente" && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">URGENTE</span>}
              {r.priority === "prioritario" && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">PRIORITÁRIO</span>}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">{r.specialty || r.type} · {r.modality || "—"}</div>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{r.desired_date}{r.desired_time && ` ${r.desired_time}`}</span>
              {r.notes && <span className="text-slate-400 italic truncate max-w-xs">"{r.notes}"</span>}
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status]}`}>{r.status?.replace("_", " ")}</span>
          {renderActions && <div className="flex gap-2">{renderActions(r)}</div>}
        </div>
      ))}
    </div>
  </section>
);
